/**
 * Public NFT verification page — /verify/:collectible_id
 *
 * Shareable proof that a teen earned and owns a specific HiBFF Collectible.
 * Designed to look credible to a stranger (parents, schools, future
 * employers) while staying playful enough that a 14-year-old wants to
 * post the link to their friends.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Award, ArrowLeft, CheckCircle2, Clock, Heart, MapPin, Share2, Sparkles,
  Download, ExternalLink,
} from 'lucide-react';

const VerifyCertificate = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/verify/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => setError('We couldn\'t find this certificate. The link may be private or the collectible has been archived.'));
  }, [id]);

  if (error) return <ErrorView message={error} />;
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-500" style={{ background: 'linear-gradient(180deg, #FFFBF5 0%, #FFF1DD 100%)' }}>
        Loading certificate…
      </div>
    );
  }

  const { name, description, image_url, attributes = [], minted_at, on_chain = {} } = data;
  const { first_name, state } = data.owner || {};
  const { is_mature, days_held, days_until_mature } = data.maturity || {};
  const rights = data.rights || {};

  const polygonscan = on_chain?.contractAddress
    ? `https://polygonscan.com/token/${on_chain.contractAddress}${on_chain.tokenId ? `?a=${on_chain.tokenId}` : ''}`
    : null;

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ url, title: `${first_name}'s HiBFF Collectible: ${name}` }); } catch {}
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to your clipboard.');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #FFFBF5 0%, #FFF1DD 100%)' }} data-testid="verify-page">
      {/* Top bar */}
      <nav className="border-b border-orange-100 bg-white/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold" style={{ color: '#1A1209' }}>
            <ArrowLeft size={14} /> hibff.com
          </Link>
          <span className="text-xs uppercase tracking-widest font-bold" style={{ color: '#D97706' }}>Certificate of Ownership</span>
          <button onClick={share} className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-orange-50" style={{ color: '#1A1209' }} data-testid="verify-share-btn">
            <Share2 size={12} /> Share
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          {/* Left: NFT image card */}
          <div className="bg-white rounded-3xl border border-orange-100 shadow-sm p-6">
            <div className="aspect-square rounded-2xl overflow-hidden mb-5" style={{ backgroundColor: '#FFF5EA' }}>
              {image_url ? (
                <img src={image_url} alt={name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Award size={48} style={{ color: '#F5831F' }} /></div>
              )}
            </div>
            <h1 className="text-3xl font-bold mb-2 leading-tight" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>{name}</h1>
            {description && <p className="text-sm" style={{ color: '#5C4A33' }}>{description}</p>}
            {attributes.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {attributes.map((a, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ backgroundColor: '#FFF1DD', color: '#92400E' }}>
                    {a.trait_type}: {a.value}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Right: Owner + status */}
          <div className="space-y-4">
            <div className="bg-white rounded-3xl border border-orange-100 shadow-sm p-6">
              <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: '#D97706' }}>Owned by</p>
              <h2 className="text-3xl font-bold mb-2 leading-tight" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>{first_name || 'A HiBFF teen'}</h2>
              {state && (
                <p className="text-sm flex items-center gap-1.5" style={{ color: '#5C4A33' }}>
                  <MapPin size={12} /> {state}, USA
                </p>
              )}
            </div>

            {/* Status pill */}
            <div className="bg-white rounded-3xl border border-orange-100 shadow-sm p-6">
              <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: '#D97706' }}>Status</p>
              <div className="flex items-center gap-3 mb-4">
                {is_mature ? (
                  <>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F8EF' }}>
                      <CheckCircle2 size={18} style={{ color: '#10B981' }} />
                    </div>
                    <div>
                      <p className="text-xl font-bold" style={{ color: '#10B981' }}>MATURED</p>
                      <p className="text-xs" style={{ color: '#666' }}>Held {days_held} days · ready to share, gift, or trade</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFF1DD' }}>
                      <Sparkles size={18} style={{ color: '#F5831F' }} />
                    </div>
                    <div>
                      <p className="text-xl font-bold" style={{ color: '#F5831F' }}>GROWING</p>
                      <p className="text-xs" style={{ color: '#666' }}>{days_until_mature} days to maturity</p>
                    </div>
                  </>
                )}
              </div>
              {/* Maturity bar */}
              <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F5C99A40' }}>
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{
                    width: `${Math.min(100, Math.round((days_held / 180) * 100))}%`,
                    background: 'linear-gradient(90deg, #FFB347 0%, #F5831F 100%)',
                  }}
                />
              </div>
              <div className="mt-1 flex justify-between text-[10px]" style={{ color: '#888' }}>
                <span>Day 1</span>
                <span>Day 180 · Maturity</span>
              </div>
              {rights.rationale && (
                <p className="mt-4 text-sm leading-relaxed" style={{ fontFamily: 'Georgia, serif', color: '#3D2914' }}>
                  {rights.rationale}
                </p>
              )}
            </div>

            {/* Onchain proof */}
            <div className="bg-white rounded-3xl border border-orange-100 shadow-sm p-6">
              <p className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: '#D97706' }}>On-chain proof</p>
              <ul className="text-xs space-y-1.5" style={{ color: '#3D2914' }}>
                <li><strong>Network:</strong> Polygon mainnet</li>
                {on_chain.contractAddress && <li className="break-all"><strong>Contract:</strong> {on_chain.contractAddress}</li>}
                {on_chain.tokenId && <li><strong>Token ID:</strong> #{on_chain.tokenId}</li>}
                {minted_at && <li><strong>Minted:</strong> {new Date(minted_at).toLocaleDateString('en-US', { dateStyle: 'long' })}</li>}
              </ul>
              {polygonscan && (
                <a href={polygonscan} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#7B4FD8] hover:underline" data-testid="verify-polygonscan-link">
                  View on Polygonscan <ExternalLink size={11} />
                </a>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/public/verify/${id}/certificate.svg`}
                target="_blank" rel="noreferrer" download
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white border border-orange-200 text-[#1A1209] text-xs font-semibold rounded-xl hover:bg-orange-50"
                data-testid="verify-svg-download"
              >
                <Download size={12} /> Download certificate (SVG)
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs mt-12" style={{ color: '#888' }}>
          HiBFF Collectibles are minted to a custodial wallet, held safely until they mature, and can be traded with parental approval (16+) or independently (18+) per US state law.
        </p>
      </div>
    </div>
  );
};

const ErrorView = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(180deg, #FFFBF5 0%, #FFF1DD 100%)' }}>
    <div className="max-w-md text-center bg-white rounded-3xl border border-orange-100 p-10 shadow-sm" data-testid="verify-error">
      <Award size={28} className="mx-auto mb-4" style={{ color: '#F5831F' }} />
      <h1 className="text-xl font-bold mb-2" style={{ fontFamily: 'Georgia, serif', color: '#1A1209' }}>Certificate not found</h1>
      <p className="text-sm" style={{ color: '#5C4A33' }}>{message}</p>
      <Link to="/" className="inline-flex mt-6 items-center gap-1.5 px-5 py-3 bg-[#F5831F] text-white text-sm font-semibold rounded-xl hover:bg-[#E07318]">
        <ArrowLeft size={14} /> Back to hibff.com
      </Link>
    </div>
  </div>
);

export default VerifyCertificate;
